/* Lost & Found 2.0 template-product.js (P). Elements: lf-gallery, lf-lightbox, lf-product-form, lf-share,
   lf-recommendations, lf-recently-viewed. Uses window.LF from global.js (dialog, cart, announce, toast, money). */
(() => {
'use strict';
const d = document;
const define = (n, c) => { if (!customElements.get(n)) customElements.define(n, c); };
const lf = () => window.LF || null;
const motion = () => !matchMedia('(prefers-reduced-motion: reduce)').matches;
const announce = (msg, o) => { const L = lf(); if (L && L.announce && msg) L.announce(msg, o); };
const FIELD = 'input, select, textarea, [contenteditable="true"]';
const isField = (el) => !!(el && el.closest && el.closest(FIELD));
const padLeft = (el) => parseFloat(getComputedStyle(el).scrollPaddingLeft) || 0;

/* Gallery (P49): one scroll-snap scroller for every width; buttons, thumbnails and dots drive it; the counter and
   dots follow native swipes; ArrowLeft/Right act only while focus is inside the gallery and not in a form field (B9). */
define('lf-gallery', class extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.viewport = this.querySelector('.lf-gallery__viewport');
    if (!this.viewport) return;
    this.bound = true;
    this.slides = Array.from(this.viewport.querySelectorAll('.lf-gallery__slide'));
    this.thumbs = Array.from(this.querySelectorAll('.lf-gallery__thumb'));
    this.dots = Array.from(this.querySelectorAll('.lf-gallery__dot'));
    this.rail = this.querySelector('.lf-gallery__thumbs');
    this.counter = this.querySelector('.lf-gallery__counter');
    this.status = this.querySelector('[data-gallery-status]');
    this.prev = this.querySelector('#lf-gallery-prev');
    this.next = this.querySelector('#lf-gallery-next');
    this.idx = 0;
    this.lock = null;
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('keydown', (e) => this.onKey(e));
    this.viewport.addEventListener('scroll', () => this.onScroll(), { passive: true });
    if (this.rail) {
      /* Desktop thumbnail rail: state classes drive the edge fade in template-product.css. */
      const fade = () => {
        const r = this.rail, over = r.scrollHeight > r.clientHeight + 1;
        r.classList.toggle('is-fade', over);
        r.classList.toggle('is-top', over && r.scrollTop > 2);
        r.classList.toggle('is-end', over && r.scrollTop + r.clientHeight >= r.scrollHeight - 2);
      };
      this.rail.addEventListener('scroll', fade, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(fade).observe(this.rail); else fade();
    }
    this.set(0, {});
  }
  onClick(e) {
    const b = e.target.closest('button');
    if (!b || !this.contains(b)) return;
    if (b === this.prev || b === this.next) {
      if (b.getAttribute('aria-disabled') !== 'true') this.go(this.idx + (b === this.next ? 1 : -1), { announce: true });
    } else if (b.classList.contains('lf-gallery__thumb') || b.classList.contains('lf-gallery__dot')) {
      this.go(Number(b.dataset.index), { announce: true });
    } else if (b.classList.contains('lf-gallery__open')) {
      const i = Number(b.dataset.index);
      if (i !== this.idx && this.slides.length > 1) this.go(i, { announce: true, focus: true });
      else this.zoom(i, b);
    }
  }
  onKey(e) {
    if ((e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    if (isField(e.target) || e.target.tagName === 'VIDEO' || this.slides.length < 2) return;
    e.preventDefault();
    const onSlide = e.target.classList && e.target.classList.contains('lf-gallery__open');
    this.go(this.idx + (e.key === 'ArrowRight' ? 1 : -1), { announce: true, focus: onSlide });
  }
  go(n, o) {
    n = Math.max(0, Math.min(this.slides.length - 1, n));
    const v = this.viewport;
    this.lock = n;
    clearTimeout(this.unlock);
    this.unlock = setTimeout(() => { this.lock = null; }, 900);
    v.scrollTo({ left: this.slides[n].offsetLeft - padLeft(v), behavior: motion() ? 'smooth' : 'auto' });
    this.set(n, o || {});
  }
  onScroll() {
    clearTimeout(this.settle);
    this.settle = setTimeout(() => { this.lock = null; this.sync(); }, 110);
    if (this.lock !== null || this.raf) return;
    this.raf = requestAnimationFrame(() => { this.raf = 0; this.sync(); });
  }
  sync() {
    const v = this.viewport, max = v.scrollWidth - v.clientWidth, pad = padLeft(v);
    let n = 0, best = Infinity;
    if (max > 0 && v.scrollLeft >= max - 2) n = this.slides.length - 1;
    else this.slides.forEach((s, i) => { const dist = Math.abs(s.offsetLeft - pad - v.scrollLeft); if (dist < best) { best = dist; n = i; } });
    if (n !== this.idx) this.set(n, {});
  }
  set(n, o) {
    const was = this.idx, N = this.slides.length;
    this.idx = n;
    this.slides.forEach((s, i) => {
      const on = i === n;
      s.classList.toggle('is-active', on);
      const b = s.querySelector('.lf-gallery__open');
      if (b) b.tabIndex = on ? 0 : -1;
      const vid = s.querySelector('video');
      if (vid && !on && !vid.paused) vid.pause();
    });
    const mark = (list, cls) => list.forEach((el, i) => {
      el.classList.toggle(cls, i === n);
      if (i === n) el.setAttribute('aria-current', 'true'); else el.removeAttribute('aria-current');
    });
    mark(this.thumbs, 'lf-gallery__thumb--active');
    mark(this.dots, 'lf-gallery__dot--active');
    if (this.counter) this.counter.textContent = (n + 1) + ' / ' + N;
    if (this.prev) this.prev.setAttribute('aria-disabled', String(n === 0));
    if (this.next) this.next.setAttribute('aria-disabled', String(n === N - 1));
    const t = this.thumbs[n], r = this.rail;
    if (t && r && r.scrollHeight > r.clientHeight + 1) {
      r.scrollTo({ top: t.offsetTop - (r.clientHeight - t.offsetHeight) / 2, behavior: motion() ? 'smooth' : 'auto' });
    }
    if (o.announce && this.status && n !== was) this.status.textContent = 'Image ' + (n + 1) + ' of ' + N;
    if (o.focus) {
      const f = this.slides[n].querySelector('.lf-gallery__open, video');
      if (f) f.focus({ preventScroll: true });
    }
  }
  items() {
    return this.slides.map((s) => {
      const img = s.querySelector('img');
      return {
        type: s.dataset.mediaType, full: s.dataset.full, video: s.dataset.video || '',
        w: Number(s.dataset.w) || 0, h: Number(s.dataset.h) || 0, alt: s.dataset.alt || '',
        preview: img ? (img.currentSrc || img.src) : ''
      };
    });
  }
  opener(i) { const s = this.slides[i]; return s ? s.querySelector('.lf-gallery__open, video') : null; }
  zoom(i, trigger) {
    const lb = d.getElementById('lf-lightbox');
    if (!lb || typeof lb.open !== 'function') return;
    lb.open(this.items(), i, trigger, (last) => {
      if (last !== this.idx) {
        this.viewport.scrollTo({ left: this.slides[last].offsetLeft - padLeft(this.viewport), behavior: 'auto' });
        this.set(last, {});
      }
      return this.opener(last);
    });
  }
});

/* Lightbox (P49; fixes B1, B5): a real dialog through LF.dialog. 2400w image, click toggles 2x zoom and native
   overflow scrolling pans it, swipe and arrow keys change images, Escape closes and returns focus. */
define('lf-lightbox', class extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.stage = this.querySelector('[data-lightbox-stage]');
    if (!this.stage) return;
    this.bound = true;
    this.counter = this.querySelector('#lf-lightbox-counter');
    this.closeBtn = this.querySelector('#lf-lightbox-close');
    this.list = [];
    this.i = 0;
    this.addEventListener('click', (e) => {
      if (e.target.closest('#lf-lightbox-close')) this.close();
      else if (e.target.closest('#lf-lightbox-prev')) this.show(this.i - 1);
      else if (e.target.closest('#lf-lightbox-next')) this.show(this.i + 1);
      else if (this.img && e.target === this.img) { if (!this.moved) this.toggleZoom(e); }
      else if (e.target === this.stage && !this.zoomed && !this.moved) this.close();
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); this.close(); return; }
      if ((e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') || isField(e.target) || e.target.tagName === 'VIDEO') return;
      e.preventDefault();
      const dir = e.key === 'ArrowRight' ? 1 : -1;
      if (this.zoomed) this.stage.scrollBy({ left: dir * 120 });
      else if (this.list.length > 1) this.show(this.i + dir);
    });
    this.stage.addEventListener('pointerdown', (e) => { this.sx = e.clientX; this.sy = e.clientY; this.moved = false; });
    this.stage.addEventListener('pointermove', (e) => {
      if (this.sx != null && Math.abs(e.clientX - this.sx) + Math.abs(e.clientY - this.sy) > 12) this.moved = true;
    });
    this.stage.addEventListener('pointerup', (e) => {
      if (this.sx == null) return;
      const dx = e.clientX - this.sx, dy = e.clientY - this.sy;
      this.sx = null;
      if (!this.zoomed && this.list.length > 1 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.show(this.i + (dx < 0 ? 1 : -1));
    });
    this.stage.addEventListener('pointercancel', () => { this.sx = null; });
    this.onClosed = (e) => { if (e.detail && e.detail.el === this) this.cleanup(); };
    d.addEventListener('lf:dialog:close', this.onClosed);
  }
  disconnectedCallback() { d.removeEventListener('lf:dialog:close', this.onClosed); }
  open(list, i, trigger, onClose) {
    const L = lf();
    if (!L || !L.dialog || !list.length) return;
    this.list = list;
    this.onCloseCb = onClose;
    this.show(i);
    this.classList.add('lf-lightbox--open');
    L.dialog.open(this, { trigger, initialFocus: this.closeBtn });
  }
  show(i) {
    const N = this.list.length, it = this.list[((i % N) + N) % N];
    this.i = ((i % N) + N) % N;
    this.unzoom();
    this.stage.textContent = '';
    this.img = null;
    let el;
    if (it.type === 'video' && it.video) {
      el = d.createElement('video');
      el.className = 'lf-lightbox__video';
      el.controls = true;
      el.playsInline = true;
      el.preload = 'metadata';
      if (it.preview) el.poster = it.preview;
      el.src = it.video;
    } else {
      el = d.createElement('img');
      el.id = 'lf-lightbox-image';
      el.className = 'lf-lightbox__image';
      el.alt = it.alt;
      el.draggable = false;
      if (it.w && it.h) { el.width = it.w; el.height = it.h; }
      if (it.preview) {
        el.style.backgroundImage = 'url("' + String(it.preview).replace(/["\\]/g, '') + '")';
        el.addEventListener('load', () => { el.style.backgroundImage = ''; }, { once: true });
      }
      el.src = it.full;
      this.img = el;
    }
    this.stage.appendChild(el);
    if (this.counter) this.counter.textContent = (this.i + 1) + ' / ' + N;
  }
  toggleZoom(e) {
    if (this.zoomed) { this.unzoom(); return; }
    const img = this.img, st = this.stage, r = img.getBoundingClientRect();
    const nw = img.naturalWidth || Number(img.getAttribute('width')) || 0, nh = img.naturalHeight || Number(img.getAttribute('height')) || 0;
    if (!r.width || !r.height || !nw || !nh) return;
    const fit = Math.min(r.width / nw, r.height / nh), cw = nw * fit, ch = nh * fit;
    const clamp = (v) => Math.min(1, Math.max(0, v));
    const fx = clamp((e.clientX - r.left - (r.width - cw) / 2) / cw), fy = clamp((e.clientY - r.top - (r.height - ch) / 2) / ch);
    this.zoomed = true;
    this.classList.add('is-zoomed');
    img.style.width = Math.round(cw * 2) + 'px';
    img.style.height = Math.round(ch * 2) + 'px';
    st.scrollLeft = fx * cw * 2 - st.clientWidth / 2;
    st.scrollTop = fy * ch * 2 - st.clientHeight / 2;
  }
  unzoom() {
    if (!this.zoomed) return;
    this.zoomed = false;
    this.classList.remove('is-zoomed');
    if (this.img) { this.img.style.width = ''; this.img.style.height = ''; }
    this.stage.scrollTo(0, 0);
  }
  close() {
    const L = lf();
    if (!L || !L.dialog || !L.dialog.isOpen(this)) return;
    const back = typeof this.onCloseCb === 'function' ? this.onCloseCb(this.i) : null;
    L.dialog.close(this, back ? { returnTo: back } : {});
  }
  cleanup() {
    this.classList.remove('lf-lightbox--open');
    this.unzoom();
    const v = this.stage.querySelector('video');
    if (v) v.pause();
  }
});

/* Cart form (P56, P60, P65): adds through LF.cart.add and opens the drawer; the form still posts without JS. */
define('lf-product-form', class extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.form = this.querySelector('form');
    this.btn = this.querySelector('.lf-add-to-cart');
    if (!this.form || !this.btn) return;
    this.bound = true;
    this.text = this.btn.querySelector('[data-atc-text]');
    this.label = this.text ? this.text.textContent : '';
    this.form.addEventListener('submit', (e) => this.submit(e));
    const sel = this.form.querySelector('select[name="id"]');
    if (sel) sel.addEventListener('change', () => this.variant(sel));
  }
  reset() {
    this.btn.classList.remove('is-done', 'is-error');
    if (this.text) this.text.textContent = this.label;
  }
  submit(e) {
    const L = lf();
    if (!L || !L.cart) return;
    e.preventDefault();
    const b = this.btn;
    if (b.disabled || b.getAttribute('aria-busy') === 'true') return;
    const field = this.form.querySelector('[name="id"]');
    const title = this.dataset.title || '';
    clearTimeout(this.t);
    this.reset();
    b.classList.add('is-busy');
    b.setAttribute('aria-busy', 'true');
    L.cart.add({ id: field ? field.value : '', quantity: 1, source: 'pdp', title, openDrawer: true })
      .then(() => {
        b.classList.remove('is-busy');
        b.classList.add('is-done');
        if (this.text) this.text.textContent = this.dataset.added || '';
        announce(L.t ? L.t('added_to_cart', { title }) : '');
        this.t = setTimeout(() => this.reset(), 2200);
      })
      .catch((err) => {
        b.classList.remove('is-busy');
        b.classList.add('is-error');
        if (this.text) this.text.textContent = this.dataset.error || '';
        const reason = err && err.status === 422 && err.message ? err.message : '';
        if (reason && L.toast) L.toast(reason, { tone: 'error', announce: false });
        announce(reason || (L.t ? L.t('add_error', { title }) : this.dataset.error), { assertive: true });
        this.t = setTimeout(() => this.reset(), 2500);
      })
      .finally(() => b.removeAttribute('aria-busy'));
  }
  variant(sel) {
    const o = sel.selectedOptions[0];
    if (!o) return;
    const ok = o.dataset.available === 'true';
    const shape = d.querySelector('.lf-product-info__price .lf-pricetag__shape');
    if (shape && o.dataset.price) {
      const node = Array.from(shape.childNodes).find((x) => x.nodeType === 3 && x.data.trim());
      if (node) node.data = o.dataset.price;
    }
    this.btn.disabled = !ok;
    this.label = ok ? this.dataset.labelAdd : this.dataset.labelSold;
    this.reset();
    try { const u = new URL(location.href); u.searchParams.set('variant', sel.value); history.replaceState(history.state, '', u); } catch (err) { /* ignore */ }
  }
});

/* Share (P57): Copy link writes the canonical product URL; "Copied!" for 2 s, announced. */
define('lf-share', class extends HTMLElement {
  connectedCallback() {
    const btn = this.querySelector('#lf-copy-link-btn');
    if (this.bound || !btn) return;
    this.bound = true;
    const label = btn.querySelector('#lf-copy-link-text'), orig = label ? label.textContent : '';
    const done = () => {
      btn.classList.add('is-copied');
      if (label) label.textContent = this.dataset.copied || '';
      announce(this.dataset.copied);
      clearTimeout(this.t);
      this.t = setTimeout(() => { btn.classList.remove('is-copied'); if (label) label.textContent = orig; }, 2000);
    };
    btn.addEventListener('click', () => {
      const url = btn.dataset.url || location.href.split(/[?#]/)[0];
      const fallback = () => {
        const ta = d.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0';
        d.body.appendChild(ta);
        ta.select();
        let ok = false;
        try { ok = d.execCommand('copy'); } catch (err) { ok = false; }
        ta.remove();
        btn.focus({ preventScroll: true });
        if (ok) done();
      };
      if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(url).then(done, fallback);
      else fallback();
    });
  }
});

/* You may also like (P58; fixes B12): Section Rendering through the recommendations route, fetched as soon as the
   browser is idle after load (not on proximity), so the section is in place before a fast scroll reaches the footer
   (CLS). It stays hidden when Shopify returns nothing or the request fails. */
define('lf-recommendations', class extends HTMLElement {
  connectedCallback() {
    if (this.bound || !this.dataset.url) return;
    this.bound = true;
    (window.requestIdleCallback || ((f) => setTimeout(f, 200)))(() => this.load());
  }
  load() {
    fetch(this.dataset.url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error('HTTP ' + r.status))))
      .then((html) => {
        const src = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recs-grid]');
        const grid = this.querySelector('[data-recs-grid]');
        if (!src || !grid || !src.children.length) return;
        grid.replaceChildren(...Array.from(src.children));
        this.hidden = false;
        const L = lf();
        if (L && L.reveal) L.reveal(this);
      })
      .catch(() => { /* stays hidden (P58) */ });
  }
});

/* Recently viewed (P59): localStorage lf_recently_viewed, at most 8 entries, current product first. */
const RV_KEY = 'lf_recently_viewed', RV_MAX = 8;
const rvRead = () => {
  try {
    const v = JSON.parse(localStorage.getItem(RV_KEY) || '[]');
    return Array.isArray(v) ? v.filter((x) => x && typeof x.handle === 'string') : [];
  } catch (err) { return []; }
};
const rvWrite = (list) => { try { localStorage.setItem(RV_KEY, JSON.stringify(list)); } catch (err) { /* quota or blocked */ } };
const safePath = (u) => (typeof u === 'string' && /^\/(?!\/)/.test(u) ? u : '');
const safeImg = (u) => (typeof u === 'string' && /^(https:)?\/\//.test(u) ? u : '');
const SIZES = '(min-width: 1280px) 290px, (min-width: 820px) 23vw, 46vw';

define('lf-recently-viewed', class extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    const grid = this.querySelector('[data-rv-grid]');
    if (!grid) return;
    const cur = this.dataset.current, lim = Number(this.dataset.limit) || 4;
    const items = rvRead().filter((p) => p.handle !== cur && safePath(p.url) && p.title).slice(0, lim);
    if (!items.length) return;
    grid.replaceChildren(...items.map((p) => this.card(p)));
    this.hidden = false;
  }
  card(p) {
    const el = (tag, cls, txt) => { const n = d.createElement(tag); if (cls) n.className = cls; if (txt != null) n.textContent = txt; return n; };
    const card = el('article', 'lf-product-card lf-product-card--grid');
    const media = el('div', 'lf-product-card__media');
    const src = safeImg(p.image);
    if (src) {
      const img = el('img', 'lf-product-card__img');
      if (/[?&]width=\d+/.test(src)) {
        img.srcset = [240, 360, 480, 600].map((w) => src.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w').join(', ');
        img.sizes = SIZES;
      }
      img.src = src;
      img.alt = String(p.title);
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 600;
      img.height = 800;
      media.appendChild(img);
    } else {
      media.appendChild(el('div', 'lf-product-card__placeholder', this.dataset.noImage || ''));
    }
    const badge = p.available === false ? el('span', 'lf-badge lf-badge--sold', this.dataset.sold)
      : p.inStore === true ? el('span', 'lf-badge lf-badge--store', this.dataset.inStore) : null;
    if (badge) { const b = el('div', 'lf-product-card__badges'); b.appendChild(badge); media.appendChild(b); }
    card.appendChild(media);
    const body = el('div', 'lf-product-card__body');
    if (p.vendor) {
      const w = el('span', 'lf-woven lf-woven--sm');
      w.title = String(p.vendor);
      w.appendChild(el('span', 'lf-visually-hidden', (this.dataset.booth || '') + ' '));
      w.appendChild(el('span', 'lf-woven__text', String(p.vendor)));
      body.appendChild(w);
    }
    const h = el('h3', 'lf-product-card__title');
    const a = el('a', 'lf-product-card__link', String(p.title));
    a.href = safePath(p.url);
    h.appendChild(a);
    body.appendChild(h);
    const L = lf();
    if (p.price != null && L && L.money) {
      const price = el('div', 'lf-price');
      price.appendChild(el('span', 'lf-price__current', L.money(p.price)));
      body.appendChild(price);
    }
    card.appendChild(body);
    return card;
  }
});

const recordView = () => {
  const el = d.getElementById('lf-product-json');
  if (!el) return;
  let p;
  try { p = JSON.parse(el.textContent); } catch (err) { return; }
  if (!p || !p.handle) return;
  rvWrite([p].concat(rvRead().filter((x) => x.handle !== p.handle)).slice(0, RV_MAX));
};
recordView();
})();
